/* Command v2 — answers "what needs me right now?" the moment it opens.
   The Needs-you card tops the surface (pending approvals + red alerts +
   budget warnings, each deep-linking to where Cassidy can act), followed by
   the glance widgets: health ribbon, AI budget, live activity, quick stats,
   crew online, daily brief. The whole surface is a customizable widget grid
   (move / hide / add, persisted) — detailed action buttons live on
   Approvals & Actions, not here. Sources: health, budget, activity, site,
   crew, approvals. */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HealthTile, BudgetGauge, Sparkline, WidgetBody } from '../../ds/mc-kit';
import { ActivityItem, StatusPill, Badge } from '../../ds';
import { useContract } from '../../lib/api';
import { useOps } from '../ops';
import { Widget, Panel, Kicker } from '../ui';
import { useWidgetGrid, WidgetGridView, type WidgetDef } from '../widgets';
import { HeartbeatChip } from '../Heartbeat';
import { integrationFor } from '../../lib/flags';
import { deriveActivity, deriveCrewOnline, deriveNeedsYou, deriveDailyBrief, tone as toneColor, toneSoft, toneLine } from '../../lib/selectors';
import type { HealthTile as HealthTileT } from '../../lib/contract';

const statCard: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '15px 17px', minWidth: 0 };
const statHead: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 };
const statLabel: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)' };
const statBig: React.CSSProperties = { fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 26, letterSpacing: '-0.02em', color: 'var(--ink)' };
const statFoot: React.CSSProperties = { marginTop: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)' };

const KIND_WORD: Record<string, string> = { approval: 'approve', alert: 'red alert', budget: 'budget' };

export function Command() {
  const { pushToast } = useOps();
  const nav = useNavigate();
  const health = useContract('health', { pollMs: 20_000 });
  const budget = useContract('budget');
  const activity = useContract('activity', { pollMs: 15_000 });
  const site = useContract('site');
  const crew = useContract('crew', { pollMs: 20_000 });
  const approvals = useContract('approvals', { pollMs: 20_000 });

  const budgetPct = budget.data?.pct ?? 0;
  const budgetTone = budgetPct >= 90 ? 'red' : budgetPct >= 70 ? 'amber' : 'ok';
  const crewList = crew.data ?? [];
  const running = crewList.filter((c) => c.status === 'running').length;
  const active = crewList.filter((c) => c.status !== 'idle').length;

  const needsYou = useMemo(
    () => deriveNeedsYou({ approvals: approvals.data, health: health.data, budget: budget.data }),
    [approvals.data, health.data, budget.data],
  );
  const brief = useMemo(
    () => deriveDailyBrief({ health: health.data, budget: budget.data, approvals: approvals.data, activity: activity.data, crew: crew.data }),
    [health.data, budget.data, approvals.data, activity.data, crew.data],
  );

  const defs: WidgetDef[] = [
    {
      id: 'needs-you',
      title: 'Needs you',
      desc: 'Approvals, red alerts and budget warnings — your triage queue.',
      span: 'full',
      node: (
        <Panel
          accent
          label="Needs you"
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Kicker>// needs you</Kicker>
              {needsYou.length > 0 && (
                <span aria-hidden="true" style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'var(--primary)', color: 'var(--on-primary)', fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, display: 'inline-grid', placeItems: 'center' }}>
                  {needsYou.length}
                </span>
              )}
            </span>
          }
          aside={approvals.isPlaceholder ? <Badge tone="warn">sample data</Badge> : undefined}
        >
          {needsYou.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--muted)', fontSize: 13.5, padding: '6px 2px' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flex: 'none' }} aria-hidden="true" />
              Nothing needs you right now — the crew is clear to run.
            </div>
          ) : (
            <div role="list" aria-label={`${needsYou.length} items need you`}>
              {needsYou.map((n) => (
                <button
                  key={n.id}
                  role="listitem"
                  onClick={() => nav(n.href)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
                    background: 'none', border: 'none', borderBottom: '1px solid var(--border)',
                    padding: '10px 2px', cursor: 'pointer', color: 'var(--ink)', minHeight: 44,
                  }}
                >
                  <span aria-hidden="true" style={{ width: 26, height: 26, flex: 'none', borderRadius: 8, display: 'grid', placeItems: 'center', fontSize: 13, background: toneSoft(n.tone), border: `1px solid ${toneLine(n.tone)}` }}>
                    {n.glyph ?? <span style={{ width: 8, height: 8, borderRadius: '50%', background: toneColor(n.tone) }} />}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.text}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.sub}</span>
                  </span>
                  <span style={{ flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: toneColor(n.tone) === 'var(--warn)' ? 'var(--warn)' : toneColor(n.tone), display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: toneColor(n.tone) }} />
                    {KIND_WORD[n.kind]}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Panel>
      ),
    },
    {
      id: 'health',
      title: 'System health',
      desc: 'The five-system status ribbon.',
      span: 'full',
      node: (
        <WidgetBody
          state={health.state}
          minHeight={128}
          rows={1}
          errorLabel="Health unavailable"
          errorSub="Couldn’t reach the status sources."
          emptyLabel="No systems monitored"
          onRetry={health.refetch}
        >
          <div className="mc-ribbon" style={{ '--mc-count': 5 } as React.CSSProperties}>
            {(health.data ?? []).map((h: HealthTileT) => (
              <HealthTile key={h.id} name={h.name} sub={h.sub} status={h.status} metric={h.metric} detail={h.detail} icon={h.icon} onClick={() => pushToast('info', h.name, `${h.metric} · ${h.detail}`)} />
            ))}
          </div>
        </WidgetBody>
      ),
    },
    {
      id: 'budget',
      title: 'AI budget',
      desc: 'Spend gauge for the current cycle.',
      span: 'half',
      node: (
        <Widget
          query={budget}
          title="// ai budget"
          accent
          minHeight={150}
          rows={2}
          aside={<StatusPill status="running" label={`${budgetTone} · ${budgetPct}%`} dot={false} />}
          errorLabel="Budget unavailable"
          errorSub="Couldn’t reach the billing API."
          emptyLabel="No spend this cycle"
          connect={integrationFor('budget')?.connect}
          badge="coming soon"
        >
          {(b) => <BudgetGauge {...b} />}
        </Widget>
      ),
    },
    {
      id: 'activity',
      title: 'Live activity',
      desc: 'The event stream across agents, CI, deploys and the site.',
      span: 'half',
      node: (
        <Widget
          query={activity}
          title="// live activity"
          minHeight={150}
          rows={4}
          errorLabel="Feed disconnected"
          errorSub="The event stream dropped."
          emptyLabel="No activity yet"
          connect={integrationFor('activity')?.connect}
          badge="connect me"
        >
          {(a) => (
            <div className="mc-scroll" tabIndex={0} role="region" aria-label="Live activity feed" style={{ maxHeight: 252, overflow: 'auto', paddingRight: 4 }}>
              {deriveActivity({ activity: a }).map((ev, i) => (
                <ActivityItem
                  key={i}
                  tone={ev.tone}
                  title={ev.actor ? <span><b style={{ color: 'var(--ink)', fontWeight: 600 }}>{ev.actor}</b> {ev.text}</span> : ev.text}
                  meta={ev.sys}
                  time={ev.time}
                  last={ev.last}
                />
              ))}
            </div>
          )}
        </Widget>
      ),
    },
    {
      id: 'stats',
      title: 'Quick stats',
      desc: 'Requests, visitors, subscribers, error rate.',
      span: 'full',
      node: (
        <WidgetBody state={site.state} minHeight={92} rows={1} errorLabel="Analytics offline" emptyLabel="No traffic yet" onRetry={site.refetch}>
          {site.data && (
            <div className="mc-auto mc-auto-md">
              <div style={statCard}>
                <div style={statHead}><span style={statLabel}>Requests · 24h</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>{site.data.requestsDelta}</span></div>
                <div style={statBig}>{site.data.requestsTotal}</div>
                <div style={{ marginTop: 10 }}><Sparkline data={site.data.requests} height={30} tone="cyan" /></div>
              </div>
              <div style={statCard}>
                <div style={statHead}><span style={statLabel}>Visitors today</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>{site.data.visitorsDelta}</span></div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}><span style={statBig}>{site.data.visitors}</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)' }}>· {site.data.online} online</span></div>
                <div style={statFoot}>people on the site right now</div>
              </div>
              <div style={statCard}>
                <div style={statHead}><span style={statLabel}>Subscribers</span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-ink)' }}>{site.data.subsDelta}</span></div>
                <div style={statBig}>{site.data.subscribers.toLocaleString()}</div>
                <div style={statFoot}>newsletter · this week</div>
              </div>
              <div style={statCard}>
                <div style={statHead}><span style={statLabel}>Error rate</span><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--danger)' }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--danger)' }} />watch</span></div>
                <div style={statBig}>{site.data.errorRate}</div>
                <div style={statFoot}>edge · last 24h</div>
              </div>
            </div>
          )}
        </WidgetBody>
      ),
    },
    {
      id: 'crew',
      title: 'Crew online',
      desc: 'Who is running, blocked or idle right now.',
      span: 'half',
      node: (
        <Widget
          query={crew}
          title="// crew online"
          minHeight={150}
          rows={4}
          aside={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-ink)' }}>{running} running · {active} active</span>}
          errorLabel="Crew offline"
          errorSub="Couldn’t reach the orchestrator."
          emptyLabel="Crew is idle"
          connect={integrationFor('crew')?.connect}
          badge="connect me"
        >
          {(c) => (
            <div>
              {deriveCrewOnline({ crew: c }).map((a) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ width: 32, height: 32, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 16, flex: 'none', background: a.lead ? 'var(--primary-soft)' : 'var(--accent-soft)', border: `1px solid ${a.lead ? 'var(--primary-line)' : 'var(--accent-line)'}` }}>{a.glyph}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 13.5, color: 'var(--ink)' }}>{a.name}</span>
                      {a.lead && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--primary)' }}>lead</span>}
                    </span>
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.task}</span>
                  </span>
                  <StatusPill status={a.status} />
                </div>
              ))}
            </div>
          )}
        </Widget>
      ),
    },
    {
      id: 'brief',
      title: 'Daily brief',
      desc: 'The morning summary — health, budget, approvals, crew.',
      span: 'half',
      node: (
        <Panel title="// daily brief" label="Daily brief" aside={<Kicker style={{ color: 'var(--accent-ink)' }}>{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</Kicker>}>
          <p style={{ margin: '0 0 10px', fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14.5, color: 'var(--ink)', lineHeight: 1.45 }}>{brief.headline}</p>
          <div>
            {brief.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 0', borderTop: i ? '1px solid var(--border)' : 'none', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: toneColor(l.tone), flex: 'none', marginTop: 5 }} />
                <span style={{ minWidth: 0 }}>{l.text}</span>
              </div>
            ))}
          </div>
        </Panel>
      ),
    },
  ];

  const grid = useWidgetGrid('command', defs);

  return (
    <div data-screen-label="Command" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--mc-gap)' }}>
      {/* Machine online chip — is the always-on box (Forge) beating right now? */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <HeartbeatChip />
      </div>
      <WidgetGridView grid={grid} label="Command" />
    </div>
  );
}
